import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CalendarIcon, ArrowRightIcon } from '../components/icons/Icons';
import { useSettings } from '../context/SettingsContext';
import api from '../services/api';
import './Blog.css';

const Blog = () => {
  const { settings } = useSettings();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1 });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async (page = 1) => {
    try {
      setLoading(true);
      const response = await api.get(`/blog?page=${page}&limit=9`);
      setPosts(response.data.posts);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <Helmet>
        <title>Blog | Fuwali Investments Zambia Limited</title>
        <meta name="description" content="Stay updated with the latest news, insights, and updates from Fuwali Investments Zambia Limited." />
        <link rel="canonical" href="https://fuwalizambia.com/blog" />
        <meta property="og:title" content="Blog | Fuwali Investments Zambia Limited" />
        <meta property="og:description" content="Stay updated with the latest news, insights, and updates from Fuwali Investments Zambia Limited." />
        <meta property="og:url" content="https://fuwalizambia.com/blog" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Page Hero */}
      <section className="page-hero" style={settings.hero_bg_blog ? { backgroundImage: `url(${settings.hero_bg_blog})` } : {}}>
        <div className="page-hero-overlay"></div>
        <div className="container">
          <span className="page-hero-label">Our Blog</span>
          <h1 className="page-hero-title">News & Insights</h1>
          <p className="page-hero-subtitle">
            Stay updated with the latest news, insights, and updates from Fuwali Investments.
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="section blog-section">
        <div className="container">
          {loading ? (
            <div className="blog-loading">
              <div className="loading-spinner-large"></div>
              <p>Loading posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="blog-empty">
              <h3>No posts yet</h3>
              <p>Check back soon for updates and insights from our team.</p>
            </div>
          ) : (
            <>
              <div className="blog-grid">
                {posts.map(post => (
                  <article key={post.id} className="blog-card card">
                    <div className="blog-card-image">
                      {post.featured_image ? (
                        <img src={post.featured_image} alt={post.title} />
                      ) : (
                        <div className="blog-card-placeholder">
                          <span>Fuwali</span>
                        </div>
                      )}
                    </div>
                    <div className="blog-card-content">
                      <div className="blog-card-meta">
                        <span className="blog-card-date">
                          <CalendarIcon size={14} />
                          {formatDate(post.published_at)}
                        </span>
                        {post.author && (
                          <span className="blog-card-author">By {post.author}</span>
                        )}
                      </div>
                      <h3 className="blog-card-title">
                        <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                      </h3>
                      {post.excerpt && (
                        <p className="blog-card-excerpt">{post.excerpt}</p>
                      )}
                      <Link to={`/blog/${post.slug}`} className="blog-card-link">
                        Read More <ArrowRightIcon size={16} />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>

              {pagination.pages > 1 && (
                <div className="blog-pagination">
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      className={`pagination-btn ${pagination.page === page ? 'active' : ''}`}
                      onClick={() => fetchPosts(page)}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default Blog;
